import {
  CategoryItemListAction,
  CategoryTreeGetAction,
  KalturaAsset,
  KalturaCategoryItemListResponse,
  KalturaCategoryTree,
  KalturaMetaFilter,
  KalturaMeta,
  CategoryItemListActionArgs,
  CategoryTreeDuplicateAction,
  KalturaMediaAsset,
  KalturaMetaListResponse,
} from '@kaltura-ott/ott-client-types/api/types';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { getAssetByCategoryId } from './asset';
import { getMetasList } from './creator/metas';
import { kalturaClient } from './kaltura-clent';

/**
 * @category Category
 * @interface
 */
export interface KuxCategoryTreeDTO extends KalturaCategoryTree {
  asset: KalturaAsset | null;
  metas: KalturaMeta[];
}

/**
 * Loads category by provided category ID
 *
 * TODO: Function should return full DTO:
 *  Category
 *    - asset
 *    - metas
 *    - tags
 *  DTO should to be clean and easy to use for displaying page.
 * @category Category
 * @function
 * @param categoryItemId
 */
export function getCategoryTreeWithAssetById(categoryItemId: number): Observable<KuxCategoryTreeDTO> {
  const action = new CategoryTreeGetAction({ categoryItemId });
  const client = kalturaClient();

  return forkJoin([client.request(action), getAssetByCategoryId(categoryItemId)]).pipe(
    mergeMap((responses: [KalturaCategoryTree, KalturaMediaAsset | null]) => {
      const asset = responses[1];
      const categoryItem: KuxCategoryTreeDTO = Object.assign(responses[0], { metas: [], asset: null });
      return getMetasList(new KalturaMetaFilter({ assetStructIdEqual: asset?.type })).pipe(
        map((fields: KalturaMetaListResponse) => {
          categoryItem.asset = asset;
          categoryItem.metas = fields.objects;
          return categoryItem;
        }),
      );
    }),
    catchError((error) => {
      return of(error);
    }),
  );
}

/**
 * Build request payload for service 'categoryItem' action 'list'.
 *
 * Usage: Gets all categoryItem items
 *
 * @category Category
 * @param args
 */
export const getCategoryList = (args?: CategoryItemListActionArgs): Observable<KalturaCategoryItemListResponse> => {
  const action = new CategoryItemListAction(args);
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * Duplicates (clone) existing category with new name
 *
 * @param newCategoryName
 * @param originalCategoryId
 */
export const duplicateCategory = (
  newCategoryName: string,
  originalCategoryId: number,
): Observable<KalturaCategoryTree> => {
  const action = new CategoryTreeDuplicateAction({ name: newCategoryName, categoryItemId: originalCategoryId });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};
